import {
  ACTION_RECOMMENDATION_TYPE,
  LOCATION_TYPES,
  RECOMMENDATION_CONTENT_TYPE_KEY,
  RECOMMENDATION_SSO_LABEL_BY_VALUE,
} from '@/config/constants_recom';
import { ObjectAny } from '@/utils/utils';
import _ from 'lodash';

export function formatDate(input: any) {
  const date = new Date(input);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return `${
    months[date.getMonth()]
  } ${date.getDate()}, ${date.getFullYear()} ${('0' + date.getHours()).slice(
    -2,
  )}:${('0' + date.getMinutes()).slice(-2)}`;
}

export const isAllowedUpload = (file: File, type: string[]) => {
  if (type.indexOf(file.type) !== -1) return true;
  return false;
};

export const isAllowedUploadImage = (
  file: File,
  allowedExtensions?: string[],
) => {
  const nullRegex = /[%][0zZO]{2}/gm;
  const isProhibited = file.name.match(nullRegex) === null;

  // Default allowed extensions if none provided (backward compatibility)
  const defaultExtensions = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/svg+xml',
    'image/svg',
    'image/gif',
  ];

  const extensionsToCheck = allowedExtensions || defaultExtensions;
  const isAllowedType = extensionsToCheck.indexOf(file.type) > -1;

  return isAllowedType && isProhibited;
};

export const isAllowedUploadVideo = (file: File) => {
  const nullRegex = /[%][0zZO]{2}/gm;
  const isMP4 = file.type === 'video/mp4';
  const isProhibited = file.name.match(nullRegex) === null;
  if (!isMP4 || !isProhibited) {
    return false;
  }
  return true;
};

export const isAllowedUploadJSON = (file: File) => {
  const nullRegex = /[%][0zZO]{2}/gm;
  const isAnimation = file.type === 'application/json';
  const isProhibited = file.name.match(nullRegex) === null;
  if (!isAnimation || !isProhibited) {
    return false;
  }
  return true;
};

export const isAllowedUploadCSV = (file: File) => {
  const nullRegex = /[%][0zZO]{2}/gm;
  const isCSV1 = file.type === 'application/vnd.ms-excel';
  const isCSV2 = file.type === 'text/csv';
  const isProhibited = file.name.match(nullRegex) === null;
  if ((!isCSV1 && !isCSV2) || !isProhibited) {
    return false;
  }
  return true;
};

export const isAllowedUploadExcel = (file: File) => {
  const nullRegex = /[%][0zZO]{2}/gm;
  const isExcel =
    file.type ===
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const isProhibited = file.name.match(nullRegex) === null;
  if (!isExcel || !isProhibited) {
    return false;
  }
  return true;
};

export const isAllowedUploadZip = (file: File) => {
  const nullRegex = /[%][0zZO]{2}/gm;
  const isZip = file.type === 'application/x-zip-compressed';
  const isProhibited = file.name.match(nullRegex) === null;
  if (!isZip || !isProhibited) {
    return false;
  }
  return true;
};

export const insertSortedArray = (
  array: any[],
  value: any,
  orderBy: string | undefined = undefined,
) => {
  array.splice(_.sortedIndexBy(array, value, orderBy), 0, value);
  return array;
};

export const orderArray = (
  array: any[],
  orderBy: string[] | undefined = undefined,
  dir: (boolean | 'asc' | 'desc')[] = ['asc'],
) => {
  return _.orderBy(array, orderBy, dir);
};

export const objectIsEqual = (x: any, y: any) => {
  return _.isEqual(x, y);
};

export const fileNameRegex = /^[a-zA-Z0-9_-]+\.[a-zA-Z]{3,4}$/gm;
export function generateSSOActionData(
  countries: string[],
  irecom: ObjectAny,
  osType: string,
  actionType: string,
  action: ObjectAny,
  contentType: string,
) {
  const actionDataKey = `actionData${osType}`;
  const ssoType = irecom[`ssoType${osType}`];
  const actionData = irecom[actionDataKey];
  const actionTypeByOs = generateSSOActionTypeByOs(actionType, ssoType);
  countries.forEach(country => {
    const {
      redirectUri,
      clientId,
      scope,
      state,
      queryParamProperties,
    } = actionData[country];
    const scopeParam = scope ? encodeURI(scope.join(' ')) : undefined;
    const queryParamPropertiesParam = queryParamProperties
      ? queryParamProperties
      : undefined;
    action.actionTypeByOs = actionTypeByOs;
    action.actionByOs[country] = JSON.stringify({
      redirectUri,
      clientId,
      ...(scopeParam ? { scope: scopeParam } : {}),
      state,
      ...(contentType === RECOMMENDATION_CONTENT_TYPE_KEY.DYNAMIC
        ? queryParamPropertiesParam
          ? { queryParamProperties: queryParamPropertiesParam }
          : {}
        : {}),
    });
  });
}

function generateSSOActionTypeByOs(actionType: string, ssoType: string) {
  let actionTypeByOs = '';
  switch (actionType) {
    case ACTION_RECOMMENDATION_TYPE.WEB_LINK_SSO:
      actionTypeByOs =
        ACTION_RECOMMENDATION_TYPE.WEB_LINK +
        RECOMMENDATION_SSO_LABEL_BY_VALUE[ssoType];
      break;
  }
  return actionTypeByOs;
}

export function transformPrescriberSSOActionData(
  finalAction: ObjectAny,
  countries: string[],
  os: string,
  action: ObjectAny,
  actionKeyType: string,
) {
  const osData = finalAction[os] ? finalAction[os] : {};

  countries.forEach(country => {
    const actionData = action[country];
    let parsed: ObjectAny;

    try {
      parsed = JSON.parse(actionData);
    } catch (error) {
      console.error(error);
      console.log({ error });
      parsed = { redirectUri: '', scope: [], clientId: '' };
    }

    const {
      redirectUri = '',
      scope: rawScope = [],
      clientId = '',
      state = '',
      queryParamProperties: rawQueryParamProperties = [],
    } = parsed;
    const scope =
      rawScope && rawScope.length > 0 ? decodeURI(rawScope).split(' ') : [];
    const queryParamProperties =
      rawQueryParamProperties && rawQueryParamProperties.length > 0
        ? rawQueryParamProperties
        : [];
    const countryKey =
      actionKeyType === LOCATION_TYPES.COUNTRY
        ? country.toUpperCase()
        : country.toLowerCase();

    osData[countryKey] = {
      redirectUri,
      scope,
      clientId,
      state,
      queryParamProperties,
    };
    finalAction[os] = osData;
  });
}

export const isUriField = (data: ObjectAny, osType: string) => {
  return (
    data[`actionType${osType}`] === ACTION_RECOMMENDATION_TYPE.WEB_LINK ||
    data[`actionType${osType}`] === ACTION_RECOMMENDATION_TYPE.DEEP_LINK ||
    data[`actionType${osType}`] === ACTION_RECOMMENDATION_TYPE.APP_LINK ||
    data[`actionType${osType}`] ===
      ACTION_RECOMMENDATION_TYPE.DEEP_LINK_WITH_PARAM
  );
};
export const isSSOField = (data: ObjectAny, osType: string) => {
  // Only need to check whether contains term SSO due to complicated logic of actionType
  // Client teams want to combine action type and sso type into one single field. So we have to check the whole string for SSO keyword.
  // Once the data parsing from prescriber, we need to split into action type and sso type
  return data[`actionType${osType}`].includes('SSO');
};

export const getByteLength = (str: string) => {
  let s = str.length;
  for (let i = str.length - 1; i >= 0; i--) {
    const code = str.charCodeAt(i);
    if (code > 255) {
      s++;
    }
  }
  return s;
};
